/**
 * Class description
 *
 * Created: 2020-07-01
 * @author Jie Gao
 */
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {Translation} from "@nolimitcity/slot-game/bin/core/translation/Translation";
import {WinterHoardTextStyles} from "../../config/WinterHoardTextStyles";
import {IResizeData} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {ParsedServerData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedServerData";
import {TimelineLite} from "gsap";
import {BasicBitmapCountUp} from "../../display/BasicBitmapCountUp";
import {WinterHoardGameModuleConfig} from "../../WinterHoardGameModuleConfig";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {TextStyleOptions} from "@nolimitcity/slot-game/bin/core/text/TextStyleOptions";
import {FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {WinterHoardParsedGameData} from "../../server/data/IWinterHoardServerData";
import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {SoundPlayer} from "../../sound/SoundPlayer";
import {WinterHoardSoundConfig} from "../../WinterHoardSoundConfig";
import {WinterHoardReelSymbol} from "../../symbols/WinterHoardReelSymbol";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {WinterHoardGameConfig} from "../../WinterHoardGameConfig";
import {WinterHoard} from "../../WinterHoard";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";

export class WinterHoardFreeSpinOutroView extends BaseView{
    private readonly leftCenterX:number = -180;
    private readonly rightCenterX:number = 180;
    private _gameGraphics:PIXI.Container;
    private _youWonText:PIXI.Text;
    private _totalWin:number;
    private _bitmapText:BasicBitmapCountUp;
    private _featureSprite:PIXI.Sprite;
    private _redGlow:PIXI.Sprite;
    private _darkenPlate:PIXI.Sprite;
    private _mode:String;
    private _numberOfFreespinsPlayed:number;
    private _spinsPlayed:PIXI.Text;
    private _layer:ScalableLayer;
    private _button:ContinueButton;
    private _completeCallback?:() => void;
    protected _screenRect:PIXI.Rectangle;
    private _winBelowStack:boolean = false;

    private _totalWinStyle:TextStyleOptions = {
        fontFamily : "Open Sans",
        fontSize : 58,
        fontWeight : FontWeight.BOLD,
        align : "center",
        dropShadow : true,
        dropShadowAlpha : 0.6,
        dropShadowAngle : 89.5,
        dropShadowBlur : 6,
        dropShadowColor : "#1a1740",
        dropShadowDistance : 3,
        fill : [
            "#fff7e8",
            "#fffdec",
            "#fff0aa",
            "#ffc733"
        ],
        miterLimit : 7,
        stroke : "#bd1e96",
        strokeThickness : 1
    };
    constructor(layer:ScalableLayer) {
        super();
        this._layer = layer;
    }

    protected initAnimations():void {
        this._gameGraphics = new PIXI.Container();
        this._gameGraphics.name = "_gameGraphics";

        const verticalMargin = 10;
        this._darkenPlate = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._darkenPlate.tint = 0x000000;// AE;
        this._darkenPlate.alpha = 0.65;
        this._darkenPlate.anchor.set(0.5, 0.5);
        this._darkenPlate.position.set(360, 360);

        this._redGlow = new PIXI.Sprite(GameResources.getTextures("outroRedGlow")[0]);
        this._redGlow.anchor.set(0.5, 0.5);
        this._redGlow.position.set(this.rightCenterX,100);

        this._featureSprite = new PIXI.Sprite(GameResources.getTextures("outroRedGlow")[0]);
        this._featureSprite.anchor.set(0.5, 0.5);
        this._featureSprite.scale.set(0.7, 0.7);
        this._featureSprite.position.set(this.leftCenterX,100);

        this._youWonText = new PIXI.Text(Translation.translate("You won:"), this._totalWinStyle);
        this._youWonText.anchor.set(0.5, 0.5);
        this._youWonText.position.set(this.rightCenterX, 0);
        this._bitmapText = new BasicBitmapCountUp(WinterHoardTextStyles.FS_OUTRO_COUNT_UP, 720, new PIXI.Point(0.5, 0.5));
        this._bitmapText.position.set(this.rightCenterX, 100);
        this._spinsPlayed = new PIXI.Text("", {
            fill: 0xffffff,
            fontFamily: "Open Sans",
            wordWrap: true,
            wordWrapWidth: 550,
            fontWeight: FontWeight.LIGHT,
            fontSize: 40
        });
        this._spinsPlayed.anchor.set(0.5);
        this._spinsPlayed.position.set(this.rightCenterX, 180);

        this._button = new ContinueButton(Translation.translate("CONTINUE"));
        this._button.addClickCallback(this.internalCompleteCallback);
    }

    protected onResize(data:IResizeData):void {
        this._screenRect = new PIXI.Rectangle(0, 0, data.width / this._layer.scale.x, data.height / this._layer.scale.y);
        //Negate layer scaling offset.
        this.x = -this._layer.x / this._layer.scale.x;
        this.y = -this._layer.y / this._layer.scale.y;
        this._isResizeDirty = false;
        this._gameGraphics.position.set(360, 260);
        this._darkenPlate.scale.set(this._screenRect.width, this._screenRect.height);
        this._button.position.set(0-this._button.width*0.5, 300);
    }

    public prepare(data:ParsedServerData):void {

        //console.log("Accumulated round win: ");
        //console.log("Total win: ");
        this._totalWin = data.totalWin;
        this._mode = data.mode;
        this._numberOfFreespinsPlayed = (<WinterHoardParsedGameData>data).freespinsPlayed;

        // #287
        WinterHoardGameConfig.instance.WIN_BELOW_STAKE = (WinterHoard.api.gameClientConfiguration.belowStakeWinRestriction && (data.totalWin <= (data as WinterHoardParsedGameData).playedBetValue));
        this._winBelowStack = WinterHoardGameModuleConfig.featurePrice.isWinBelowStake();
    }


    public getStartAnimation(prepareToEnterModeCallback:() => void, completeCallback:() => void):TimelineLite {
        this._button.enable(false);
        this._completeCallback = completeCallback;
        const hasWin:boolean = (this._totalWin>0);
        const tl = new TimelineLite();
        this._spinsPlayed.visible = true;
        this._spinsPlayed.text = ` ${Translation.translate("Spins played: ")} ${this._numberOfFreespinsPlayed!.toString()} `;
        this._spinsPlayed.position.y = (hasWin ? 180 : 80);
        this._featureSprite.texture = GameResources.getTextures(`fsOutro${this._mode}`)[0];
        this._gameGraphics.addChild(this._darkenPlate, this._redGlow, this._featureSprite, this._youWonText, this._bitmapText, this._spinsPlayed, this._button);
        this._youWonText.visible = hasWin && !this._winBelowStack;
        this._bitmapText.visible = hasWin;
        Helper.shrinkTextWidth(Translation.translate("Spins played: "),this._spinsPlayed, 350);
        Helper.shrinkTextWidth(Translation.translate("You won:"),this._youWonText, 350);
        tl.add(()=>{
            this._layer.addChild(this._gameGraphics);
            // #287
            !this._winBelowStack && SoundPlayer.playEffect(WinterHoardSoundConfig.FREESPIN_END);
            SoundPlayer.pauseAmbience();
            SoundPlayer.playAmbientLoop(WinterHoardSoundConfig.instance.BASIC_GAME_AMBIANCE, 1500);
            prepareToEnterModeCallback();
        });
        // #287
        const duration: number = this._winBelowStack ? 0 : 2;
        tl.add([
            () => {
                // #287
                !this._winBelowStack && SoundPlayer.playEffect(WinterHoardSoundConfig.COUNT_UP_LOOP);
            },
            this._bitmapText.getCountAnimation(0, this._totalWin, duration)
        ], 0);
        tl.add(() => {
            SoundPlayer.stopEffect(WinterHoardSoundConfig.COUNT_UP_LOOP);
            // #287
            !this._winBelowStack && SoundPlayer.playEffect(WinterHoardSoundConfig.COUNT_UP_END);
            WinterHoardGameModuleConfig.reelBackground.switchBg("main");
            this._button.enable(true);
        });
        return tl;
    }

    public getEndAnimation(): TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        tl.add(() => {
            SlotGame.keypad.setZeroBetSpinCounter(-1);
            this._layer.removeChild(this._gameGraphics);
        });
        return tl;
    }

    private internalCompleteCallback = () => {
        if(this._completeCallback) {
            [0, 1, 2, 3, 4].forEach((rid) => {
                [0, 1, 2 , 3].forEach((sid) => {
                    const symbol:WinterHoardReelSymbol | null = <WinterHoardReelSymbol> Reels.getSymbol(rid, sid);
                    if(symbol && symbol.symName === "M1") {
                        symbol.playNormalSpine();
                    }
                });
            });

            this._button.enable(false);
            this._completeCallback();
        }
    }
}